import { useCallback, useState } from "react";
import "./new-scan-transaction.scss";
import Scan from "@shared/ui/scan/scan";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import useScreenSize from "@shared/utility/screen-size";
import { useQyGetAllMemberById } from "@factory/query/member.query";
import {
  MemberControllerGet200Response,
  MessageResponseDto,
  UtilServiceControllerGet200Response,
} from "@api/api";
import { GetMemberModel } from "@factory/model/query-model-member";
import { Tag } from "primereact/tag";
import { differenceInDays } from "date-fns";
import TextBlock from "@shared/ui/text-block/text-block";
import ParentInfo from "@core/ui/parent-info/parent-info";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { TransactionServiceFormSchema } from "@factory/form/form.rule";
import { useQyGetServiceById } from "@factory/query/service.query";
import { GetServiceModel } from "@factory/model/query-model-service";
import { MutateResponseTransactionModel } from "@factory/model/query-model-transaction";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useQyCreateTransaction } from "@factory/query/transaction.query";

/* eslint-disable-next-line */
export interface NewScanTransactionProps {}

export function NewScanTransaction() {
  const { mapCreateTransaction } = useFormApiMapper();
  const { isMobileMode } = useScreenSize();
  const navigate = useNavigate();
  const [scan, setScan] = useState(false);
  const [memberId, setMemberId] = useState("");
  const [meberServiceId, setMemberServiceId] = useState("");
  const [service, setService] = useState<GetServiceModel | null>(null);
  const [member, setMember] = useState<GetMemberModel | null>(null);
  const [remainingDays, setRemainingDays] = useState(-1);
  const { showSuccess } = useNotificationContext();

  // QUERIES
  const handleGetApiSuccess = (response: MemberControllerGet200Response) => {
    if (!response || response.data?.length === 0) {
      return;
    }

    const responseData = response.data?.[0];
    if (!responseData) {
      return;
    }

    if (
      responseData?.is_subscription &&
      responseData.subscription_start_date &&
      responseData.subscription_end_date
    ) {
      const _remainingDays = differenceInDays(
        responseData?.subscription_end_date,
        responseData?.subscription_start_date
      );
      setRemainingDays(_remainingDays);
    }

    setMember(responseData);
    setMemberServiceId((responseData as any)?.service);
  };
  useQyGetAllMemberById(memberId || "", handleGetApiSuccess);

  // QUERIES: SERVICE GET
  const handleServiceGet = (response: UtilServiceControllerGet200Response) => {
    if (!response || response.data?.length === 0) {
      return;
    }

    const responseData = response.data?.[0];
    if (!responseData) {
      return;
    }

    setService(responseData);
  };
  useQyGetServiceById(meberServiceId, !!meberServiceId, handleServiceGet);

  // QUERIES: NEW TRANSACTION
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;
    showSuccess(`New Transaction is created`);
    navigate(`../${data.code}`);
  };
  const { mutateAsync: addTransaction } =
    useQyCreateTransaction(handleApiSuccess);

  // LOCAL FUNCTIONS
  const handleSuccessScan = (value: string) => {
    setMemberId(value);
  };
  const handleCloseScan = () => {
    setScan(false);
  };
  const handleBook = () => {
    if (!service || !member) {
      return;
    }

    const selectedService = {
      member: member?.code,
      amount: service?.amount,
    } as TransactionServiceFormSchema;
    const form = mapCreateTransaction({
      service: [selectedService] as any,
      addon: [],
      isPaid: true,
    });

    addTransaction(form);
  };

  // LOCAL RENDERS
  const footerContent = (
    <div>
      {member?.is_subscription && remainingDays > 0 ? (
        <Button
          className="block w-full mt-2"
          label="Book"
          outlined
          onClick={handleBook}
        />
      ) : null}
      <Button
        className="block w-full mt-2"
        label="Close"
        severity="secondary"
        outlined
        onClick={() => navigate("../")}
      />
    </div>
  );
  const memberDetail = useCallback(() => {
    const subscribedElement = (
      <div>
        <Tag value="Active Subscription" severity="success" />
        <TextBlock label="Remaining Days" value={remainingDays.toString()} />
      </div>
    );
    const expiredElement = (
      <div>
        <Tag value="Subscription Expired" severity="danger" />
      </div>
    );
    return (
      <div className="flex flex-col justify-center mt-4">
        <ParentInfo name={`${member?.first_name} ${member?.last_name}`}>
          {member?.is_subscription ? (
            <div>{remainingDays > 0 ? subscribedElement : expiredElement}</div>
          ) : null}
        </ParentInfo>
      </div>
    );
  }, [member?.code]);

  return (
    <div id="NewScanTransaction" className="new-scan-transaction">
      <Dialog
        header="Quick Book"
        visible={true}
        style={{ width: "50vw" }}
        onHide={() => {
          navigate("../");
        }}
        closeOnEscape={true}
        footer={footerContent}
        closable={false}
        maximized={isMobileMode}
      >
        <div className="m-0">
          {scan ? (
            <Scan
              successScan={handleSuccessScan}
              closeScan={handleCloseScan}
              isCloseOnScan={true}
            />
          ) : null}
        </div>

        <div className="mb-6 mt-12">
          <div className="p-inputgroup flex-1">
            <FloatLabel>
              <InputText
                id="userCode"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
                className="text-4xl w-full"
                autoFocus
                tabIndex={0}
              />
              <label htmlFor="amount">Enter User Code</label>
            </FloatLabel>

            <span
              className="p-inputgroup-addon cursor-pointer px-8 bg-emerald-500 border border-emerald-400"
              onClick={() => setScan(true)}
            >
              <i className="pi pi-camera text-white"></i>
            </span>
          </div>

          {member ? memberDetail() : null}
        </div>
      </Dialog>
    </div>
  );
}

export default NewScanTransaction;
